"use client";

import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from "react";
import { toast } from "sonner";

import { useSession } from "@/app/hooks/useSession";
import { RecipeGridSkeleton } from "@/components/shared/RecipeGridSkeleton";
import { RecipeTileVisual } from "@/components/shared/RecipeTile";
import { Button } from "@/components/ui/button";
import { FlyInTile } from "@/components/ui/fly-in-tile";
import { SearchInput } from "@/components/ui/search-input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetSection,
  SheetTitle,
} from "@/components/ui/sheet";
import { trackCalendar } from "@/lib/analytics";
import { crossfadeVariants } from "@/lib/animations";
import {
  addDays,
  localToday,
  parseIsoDate,
  startOfWeek,
  toIsoDate,
  weekStartDowForLocale,
} from "@/lib/calendar/week";
import type { RecipeSearchPage, RecipeTileData } from "@/lib/recipes/types";
import { cn } from "@/lib/utils";

const DEFAULT_ASPECT = 4 / 5;
const COLUMN_COUNT = 3;

function distributeToColumns(items: RecipeTileData[]): RecipeTileData[][] {
  const cols: RecipeTileData[][] = Array.from({ length: COLUMN_COUNT }, () => []);
  const heights = new Array(COLUMN_COUNT).fill(0);

  for (const item of items) {
    const shortest = heights.indexOf(Math.min(...heights));
    cols[shortest].push(item);
    const ratio = item.image ? (item.imageAspectRatio ?? DEFAULT_ASPECT) : DEFAULT_ASPECT;
    heights[shortest] += 1 / ratio;
  }

  return cols;
}

interface PlanMealSheetProps {
  isOpen: boolean;
  onClose: () => void;
  cookOn: string;
  locale: string;
}

export function PlanMealSheet({ isOpen, onClose, cookOn, locale }: PlanMealSheetProps) {
  const t = useTranslations("calendar");
  const queryClient = useQueryClient();
  const { data: sessionData } = useSession();
  const userId = sessionData?.user?.id;
  const [isSubmitting, startTransition] = useTransition();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<RecipeTileData[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [activeDay, setActiveDay] = useState<string>(cookOn);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const pageRef = useRef(1);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setActiveDay(cookOn);
      setSelected(new Set());
      setQuery("");
      setResults([]);
      setHasMore(false);
      pageRef.current = 1;
      setIsLoading(true);
    }
  }, [isOpen, cookOn]);

  const days = useMemo(() => {
    const base = parseIsoDate(cookOn) ?? new Date();
    const start = startOfWeek(base, weekStartDowForLocale(locale));
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(start, i);
      return { iso: toIsoDate(d), date: d };
    });
  }, [cookOn, locale]);

  const todayIso = useMemo(() => localToday(), []);
  const weekdayShortFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }),
    [locale]
  );
  const weekdayLongFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }),
    [locale]
  );

  useEffect(() => {
    if (!isOpen) return;
    const ac = new AbortController();
    const timer = setTimeout(async () => {
      setIsLoading(true);
      pageRef.current = 1;
      try {
        const params = new URLSearchParams({ scope: "all", includePublished: "true" });
        if (query.trim()) params.set("q", query.trim());
        const res = await fetch(`/api/recipes/search?${params.toString()}`, {
          signal: ac.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as RecipeSearchPage;
        setResults(data.items);
        setHasMore(data.hasMore);
      } catch (e) {
        if (!(e instanceof DOMException && e.name === "AbortError")) {
          toast.error(t("searchError"));
        }
      } finally {
        setIsLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [query, isOpen, t]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const params = new URLSearchParams({
        scope: "all",
        includePublished: "true",
        page: String(pageRef.current),
      });
      if (query.trim()) params.set("q", query.trim());
      const res = await fetch(`/api/recipes/search?${params.toString()}`);
      if (!res.ok) return;
      const data = (await res.json()) as RecipeSearchPage;
      setResults((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        const fresh = data.items.filter((r) => !seen.has(r.id));
        return [...prev, ...fresh];
      });
      setHasMore(data.hasMore);
      pageRef.current++;
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, query]);

  useEffect(() => {
    const el = sentinelRef.current;
    const root = scrollRef.current;
    if (!el || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root, rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = () => {
    if (selected.size === 0) return;
    const ids = [...selected];
    const titleForId = new Map(results.map((r) => [r.id, r.title]));

    startTransition(async () => {
      let res: Response;
      try {
        res = await fetch("/api/calendar", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ recipeIds: ids, cookOn: activeDay }),
        });
      } catch {
        toast.error(t("planFailedNetwork"));
        return;
      }

      if (res.ok) {
        trackCalendar("meal_planned", { cookOn: activeDay });
        onClose();
        queryClient.invalidateQueries({ queryKey: ["calendar"] });
        queryClient.invalidateQueries({ queryKey: ["dashboard"] });
        return;
      }

      if (res.status === 400) {
        const data = (await res.json().catch(() => null)) as {
          error?: string;
          failures?: { recipeId: string; recipeName: string | null }[];
        } | null;
        if (data?.error === "plan_partial_failure" && data.failures?.length) {
          const names = data.failures
            .map((f) => f.recipeName ?? titleForId.get(f.recipeId) ?? f.recipeId)
            .join(", ");
          toast.error(t("planFailedAll", { names }));
          return;
        }
      }

      toast.error(t("planError"));
    });
  };

  const activeDayLabel = useMemo(() => {
    const d = parseIsoDate(activeDay);
    return d ? weekdayLongFmt.format(d) : activeDay;
  }, [activeDay, weekdayLongFmt]);

  const columns = useMemo(() => distributeToColumns(results), [results]);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => (!open && !isSubmitting ? onClose() : undefined)}>
      <SheetContent disableOutsideClick={isSubmitting} className="h-[92dvh] sm:h-[80dvh]">
        <SheetHeader>
          <SheetTitle>{t("planMeal")}</SheetTitle>
          <SheetDescription className="sr-only">{t("planSubtitleAll")}</SheetDescription>
        </SheetHeader>

        {/* Day pills */}
        <SheetSection>
          <div className="flex gap-2 overflow-x-auto sm:gap-1.5 sm:overflow-x-visible">
            {days.map(({ iso, date }) => {
              const isActive = iso === activeDay;
              const isToday = iso === todayIso;
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => setActiveDay(iso)}
                  className={cn(
                    "flex shrink-0 cursor-pointer flex-col items-center gap-0.5 rounded-xl border px-3 py-2 text-center transition-colors sm:flex-1 sm:px-1",
                    isActive
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input-border bg-input-bg text-foreground hover:bg-muted/40"
                  )}
                >
                  <span
                    className={cn(
                      "text-[10px] font-semibold uppercase tracking-wider",
                      isActive
                        ? "text-primary-foreground/90"
                        : isToday
                          ? "text-primary"
                          : "text-muted-foreground"
                    )}
                  >
                    {weekdayShortFmt.format(date)}
                  </span>
                  <span
                    className={cn(
                      "text-base font-semibold leading-none tabular-nums",
                      !isActive && isToday && "text-primary"
                    )}
                  >
                    {date.getUTCDate()}
                  </span>
                </button>
              );
            })}
          </div>
        </SheetSection>

        {/* Search bar */}
        <SheetSection className="pb-2 sm:pb-4">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder={t("searchPlaceholderAll")}
            clearLabel={t("clearSearch")}
            loading={isLoading}
          />
        </SheetSection>

        {/* Scrollable recipe grid */}
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto px-3 mb-4 sm:px-6 sm:mb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <AnimatePresence mode="wait">
            {isLoading ? (
              <RecipeGridSkeleton key="skeleton" cols={3} rows={5} className="gap-1.5" />
            ) : results.length === 0 ? (
              <motion.div
                key="empty"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={crossfadeVariants}
                className="flex h-full items-center justify-center"
              >
                <p className="text-sm text-muted-foreground">
                  {query.trim() ? t("noResults") : t("noRecipesYet")}
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="results"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={crossfadeVariants}
              >
                <div className="grid grid-cols-3 gap-1.5">
                  {columns.map((col, colIdx) => (
                    <div key={colIdx} className="flex flex-col gap-1.5">
                      {col.map((r) => {
                        const isSelected = selected.has(r.id);
                        return (
                          <FlyInTile key={r.id}>
                            <button
                              type="button"
                              onClick={() => toggle(r.id)}
                              disabled={isSubmitting}
                              aria-pressed={isSelected}
                              className={cn(
                                "relative w-full cursor-pointer rounded-lg text-left transition-all disabled:opacity-50",
                                isSelected && "ring-2 ring-primary ring-offset-0"
                              )}
                            >
                              <RecipeTileVisual
                                recipe={r}
                                sizes="(max-width: 768px) 33vw, 150px"
                                compact
                                isPublic={r.userId !== userId}
                              />
                              {isSelected && (
                                <div className="absolute end-1.5 top-1.5 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                                  <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="size-3"
                                    aria-hidden
                                  >
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                </div>
                              )}
                            </button>
                          </FlyInTile>
                        );
                      })}
                    </div>
                  ))}
                </div>
                {hasMore && (
                  <div ref={sentinelRef} className="mt-4 flex justify-center py-2">
                    {isLoadingMore && (
                      <Loader2 className="size-5 animate-spin text-muted-foreground" />
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <SheetFooter>
          <Button
            type="button"
            size="lg"
            className="w-full"
            disabled={selected.size === 0 || isSubmitting}
            onClick={submit}
          >
            {isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
            {t("ctaAddRecipes", { count: selected.size, day: activeDayLabel })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
